import React, { useState } from "react";
import { Box, Button, Grid, Step, StepLabel, Stepper, Typography, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import FortnoxIntegration from "./FortnoxIntegration";
import ShopifyIntegration from "./ShopifyIntegration";
import StarwebIntegration from "./StarwebIntegration";
import VoyadoIntegration from "./VoyadoIntegration";
import DefaultIntegration from "./DefaultIntegration";
import { IntegrationSystemViewModel } from "../../../Types/ExportTypes";

interface Props {
    cancelWizard: () => void;
    create: (args?: { id?: number | null; model?: IntegrationSystemViewModel | null }) => Promise<void>;
}

const AddIntegrationWizard: React.FC<Props> = ({ cancelWizard,create }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [integration, setIntegration] = useState<string | null>(null);
    const [viewModel, setViewModel] = useState<IntegrationSystemViewModel>({});
    const prev = () => setStep((s) => Math.max(s - 1, 0));

    const selectIntegration = (type: string) => {
        setIntegration(type);
        setStep(1);
    };

    const finish = () => {
        cancelWizard();
        switch (integration) {
            case "Fortnox":
                return create({ model: viewModel });
            case "Shopify":
                return create({ model: viewModel });
            case "Starweb":
                return create({ model: viewModel });
            case "Voyado":
                return create({ model: viewModel });
            default:
                return create();
        }
   
 
    };

    const renderContent = () => {
        if (step === 0) {
            return (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ textAlign: "center" }}>{t("dialog.chooseIntegrationType")}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => selectIntegration("Fortnox")}>Fortnox</Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => selectIntegration("Shopify")}>Shopify</Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => selectIntegration("Starweb")}>Starweb</Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => selectIntegration("Voyado")}>Voyado</Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button fullWidth variant="contained" onClick={() => finish()}>{t("common.default")}</Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t("help.defaultIntegrationFlavour")}
                        </Alert>
                    </Grid>
                </Grid>
            );
        }
        if (step === 1) {
            switch (integration) {
                case "Fortnox":
                    return <FortnoxIntegration viewModel={viewModel} setViewModel={setViewModel} />;
                case "Shopify":
                    return <ShopifyIntegration viewModel={viewModel} setViewModel={setViewModel} />;
                case "Starweb":
                    return <StarwebIntegration viewModel={viewModel} setViewModel={setViewModel} />;
                case "Voyado":
                    return <VoyadoIntegration viewModel={viewModel} setViewModel={setViewModel} />;
                default:
                    return <DefaultIntegration />;
            }
        }
        return null;
    };

    return (
        <Box sx={{ width: "100%", maxWidth: 500 }}>
            <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
                <Step><StepLabel>Integration</StepLabel></Step>
                <Step><StepLabel>{t("common.details")}</StepLabel></Step>
            </Stepper>
            {renderContent()}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1 }}>
                {step > 0 && <Button onClick={prev}>{t("common.previous")}</Button>}
                {step === 1 && <Button variant="contained" color="primary" onClick={finish}>{t("common.finish")}</Button>}
                <Button variant="outlined" color="warning" onClick={cancelWizard}>{t("common.cancelWizard")}</Button>
            </Box>
        </Box>
    );
};

export default AddIntegrationWizard;
